import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Inbox,
  CheckCircle2,
  Clock,
  XCircle,
  Trash2,
  Mail,
  MailOpen,
  Copy,
  Check,
  RefreshCw,
  Loader2,
  MessageSquare,
  ArrowLeft,
  PlusCircle,
  Megaphone,
  Link2,
  TrendingUp,
  QrCode,
  Download,
  X,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ClientUpdatesPage — dashboard inbox of client-submitted status updates.
 * List view (left) + detail view (right on desktop, drawer on mobile).
 * Also exposes the shareable public link so the owner can copy & send it.
 */
export default function ClientUpdatesPage() {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  // Feb 2026 — user requested a two-folder inbox: unchecked (default)
  // vs. checked (already reviewed via the Done button).  Clicking a
  // client now opens a MODAL detail view instead of the old side pane
  // so mobile users don't have to scroll all the way back up.
  const [folder, setFolder] = useState('inbox'); // 'inbox' | 'checked'
  // 2026-02-28 — user has repeatedly complained the old lightbox/preview
  // modal gets stuck on iOS and forces app restarts.  Ripped it out
  // entirely: images now render INLINE directly under the client message
  // (no clicking, no modal).  Non-image files keep a simple link that
  // opens in a new tab.  `brokenImages` still tracks <img> onError so
  // legacy HEIC uploads gracefully degrade to a plain download link.
  const [brokenImages, setBrokenImages] = useState(() => new Set());
  const [detailOpen, setDetailOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [announceState, setAnnounceState] = useState('idle'); // idle | previewing | sending | done | error
  const [announcePreview, setAnnouncePreview] = useState(null);
  const [announceResult, setAnnounceResult] = useState(null);
  // Score-over-time history for the currently selected client.
  // { points: [{submittedAt, experian, equifax, transunion}], clientName, loading }
  const [history, setHistory] = useState({ points: [], clientName: '', loading: false });

  // Feb 2026 — new "📬 Messages" tab: client-submitted questions.
  const [tab, setTab] = useState('updates'); // 'updates' | 'messages'
  const [questions, setQuestions] = useState([]);
  const [questionsUnread, setQuestionsUnread] = useState(0);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  // Feb 2026 — pin the portal URL to the PRODUCTION domain so it always
  // matches the iPhone Text Replacement shortcut the owner has saved on
  // their device.  Previously we used window.location.origin, which meant
  // opening this page from the preview environment would generate a
  // preview-domain QR / link — mismatched from what clients actually see.
  // Clients always land on production, so the QR/link should always
  // encode the production URL regardless of which environment the owner
  // is admin'ing from.
  const PORTAL_ORIGIN = 'https://pdf-gen-preview-1.emergent.host';
  const publicUrl = `${PORTAL_ORIGIN}/client-update`;

  // Feb 2026 — QR-code modal.  Fixes the "Could not find an appropriate
  // application" Android error some clients hit when tapping raw URLs in
  // RCS/Google Messages.  QR codes bypass Android's link-handler entirely:
  // the client points their camera at the code and their default browser
  // opens the portal directly.  Owner can generate a QR for ANY URL they
  // paste, not just the portal link.
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState(publicUrl);
  const qrCanvasRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/client-updates`, { timeout: 15000 });
      setItems(data.items || []);
      setUnread(data.unreadCount || 0);
    } catch (e) {
      console.error('load client updates failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Load questions on-demand when the user opens the Messages tab.
  const loadQuestions = useCallback(async () => {
    setQuestionsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/client-questions`, { timeout: 15000 });
      setQuestions(data.items || []);
      setQuestionsUnread(data.unreadCount || 0);
    } catch (e) {
      console.error('load client questions failed', e);
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'messages') loadQuestions();
  }, [tab, loadQuestions]);

  // Refresh the top-nav unread badge on first mount so the tab badge
  // shows the correct count before the user opens the tab.
  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const selectQuestion = async (q) => {
    setSelectedQuestionId(q.id);
    if (!q.read) {
      try {
        await axios.patch(`${API_URL}/api/client-questions/${q.id}?read=true`);
        setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, read: true } : x)));
        setQuestionsUnread((c) => Math.max(0, c - 1));
      } catch (e) {
        console.error(e);
      }
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/client-questions/${id}`);
      setQuestions((prev) => prev.filter((x) => x.id !== id));
      if (selectedQuestionId === id) setSelectedQuestionId(null);
    } catch (e) {
      console.error(e);
    }
  };

  const markQuestionReplied = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/client-questions/${id}?read=true&replied=true`);
      setQuestions((prev) => prev.map((x) => (x.id === id ? { ...x, replied: true, read: true } : x)));
    } catch (e) {
      console.error(e);
    }
  };

  const selectItem = async (item) => {
    setSelectedId(item.id);
    setDetailOpen(true);
    if (!item.read) {
      try {
        await axios.patch(`${API_URL}/api/client-updates/${item.id}?read=true`);
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: true } : i)));
        setUnread((c) => Math.max(0, c - 1));
      } catch (e) {
        console.error(e);
      }
    }
    // Fetch chronological score history for this client so the detail
    // pane can plot a per-bureau trend line.  Uses linkedClientId when
    // available (accurate); otherwise falls back to fuzzy name match.
    setHistory({ points: [], clientName: '', loading: true });
    try {
      const params = new URLSearchParams({
        firstName: item.firstName || '',
        lastName: item.lastName || '',
      });
      if (item.linkedClientId) params.set('linkedClientId', item.linkedClientId);
      const { data } = await axios.get(
        `${API_URL}/api/client-updates/history?${params.toString()}`,
        { timeout: 10000 },
      );
      setHistory({
        points: data.points || [],
        clientName: data.clientName || '',
        loading: false,
      });
    } catch (e) {
      console.error('history fetch failed', e);
      setHistory({ points: [], clientName: '', loading: false });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this update? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/client-updates/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      if (selectedId === id) {
        setSelectedId(null);
        setDetailOpen(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Feb 2026 — mark this update as "checked" (reviewed).  Called by
  // the Done button inside the detail modal.  Moves the update from
  // the Inbox folder to the Checked folder and closes the modal so
  // the admin doesn't have to scroll all the way back up on mobile.
  const markChecked = async (id, checked = true) => {
    try {
      await axios.patch(`${API_URL}/api/client-updates/${id}?checked=${checked}`);
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, checked } : i)),
      );
      if (checked) {
        setDetailOpen(false);
        setSelectedId(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // ── 2026-07-22 revamp — TWO separate copies ─────────────────────
  // User asked for the ability to send the descriptive message and the
  // clickable URL as TWO separate SMS / email messages, because when
  // they're combined into one paragraph the destination app collapses
  // the newlines and swallows the link inside the body text.  Splitting
  // into two clipboards guarantees:
  //   1. The description gets pasted first (formatted paragraph)
  //   2. The URL gets pasted next as its OWN standalone message — SMS
  //      + email clients auto-linkify a lone URL on its own line and
  //      show it as a big tappable button.
  const [copiedKind, setCopiedKind] = useState(null); // "message" | "link" | null
  // Feb 2026 — quick email-invite state. Lets you paste a client's email
  // and fire off an HTML invite with a big glowing button.
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const sendEmailInvite = async () => {
    const target = inviteEmail.trim();
    if (!target.includes("@")) {
      setInviteResult({ ok: false, msg: "Enter a valid email first" });
      return;
    }
    setInviteSending(true);
    setInviteResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/client-updates/send-invite-email`, {
        to_email: target,
        to_name: inviteName.trim(),
      });
      setInviteResult({ ok: true, msg: `Sent to ${data.sent_to}` });
      setInviteEmail("");
      setInviteName("");
    } catch (e) {
      setInviteResult({ ok: false, msg: e?.response?.data?.detail || "Send failed" });
    } finally {
      setInviteSending(false);
      setTimeout(() => setInviteResult(null), 5000);
    }
  };

  const copyMessageText = async () => {
    // Descriptive paragraph WITHOUT the URL — followed by a clear line
    // telling the client the link is coming in the next message.
    const message =
`Hi! I've set up a private client portal just for you — a place to update me every week on where your credit dispute stands.

Every Friday (or any time you check your credit) tap the link and let me know:
• Which accounts were REMOVED
• Which are still PENDING investigation
• Which are still REMAINING on your report
• Any NEW accounts that showed up (I can dispute those too)
• Your latest bureau scores if you have them
• You can even upload photos of any letters you got from the bureaus or creditors (up to 5)

It takes about 60 seconds and I get an instant notification the moment you submit — so we can jump right into your next round.

Same link works every single week — save it on your phone!

👉 The clickable link is in the next message — tap it to open your portal.`;
    try {
      await navigator.clipboard.writeText(message);
      setCopiedKind("message");
      setCopied(true);
      setTimeout(() => { setCopied(false); setCopiedKind(null); }, 2500);
    } catch (e) { console.error(e); }
  };

  const copyLinkOnly = async () => {
    // Just the URL — pasted alone on its own line so SMS/email
    // clients auto-detect and render it as a big tappable link.
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedKind("link");
      setCopied(true);
      setTimeout(() => { setCopied(false); setCopiedKind(null); }, 2500);
    } catch (e) { console.error(e); }
  };

  // Kept for backwards compat — anywhere still calling copyLink() gets
  // the combined message + link paste (message on top, URL isolated at
  // the bottom between ━━━ dividers).
  const copyLink = async () => {
    const message =
`Hi! I've set up a private client portal just for you — a place to update me every week on where your credit dispute stands.

Every Friday (or any time you check your credit) tap the link and let me know:
• Which accounts were REMOVED
• Which are still PENDING investigation
• Which are still REMAINING on your report
• Any NEW accounts that showed up (I can dispute those too)
• Your latest bureau scores if you have them
• You can even upload photos of any letters you got from the bureaus or creditors (up to 5)

It takes about 60 seconds and I get an instant notification the moment you submit — so we can jump right into your next round.

Same link works every single week — save it on your phone!

👉 TAP THE LINK BELOW 👇

━━━━━━━━━━━━━━━━━━━━

${publicUrl}

━━━━━━━━━━━━━━━━━━━━`;
    try {
      await navigator.clipboard.writeText(message);
      setCopiedKind("both");
      setCopied(true);
      setTimeout(() => { setCopied(false); setCopiedKind(null); }, 2500);
    } catch (e) {
      console.error(e);
    }
  };

  // Portal-announcement blast — 2-step confirm flow so the owner sees
  // exactly WHO is about to receive the email before it fires.
  const previewAnnouncement = async () => {
    setAnnounceState('previewing');
    setAnnounceResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/quick-reminders/send-portal-announcement-now?dry_run=true`,
      );
      setAnnouncePreview(data);
    } catch (e) {
      setAnnounceResult({ ok: false, error: e?.response?.data?.detail || 'Preview failed' });
      setAnnounceState('error');
    }
  };
  const confirmAnnouncement = async () => {
    setAnnounceState('sending');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/quick-reminders/send-portal-announcement-now`,
        {},
        { timeout: 60000 },
      );
      // Backend now queues the blast in the background so we get an
      // immediate response even for large recipient lists.  A single
      // consolidated summary email fires when the job finishes.
      setAnnounceResult({
        ok: true,
        sent: data.will_send_to ?? data.emails_sent ?? 0,
        total: data.clients_found ?? 0,
        queued: !!data.queued,
      });
      setAnnounceState('done');
    } catch (e) {
      setAnnounceResult({ ok: false, error: e?.response?.data?.detail || 'Send failed' });
      setAnnounceState('error');
    }
  };
  const closeAnnounce = () => {
    setAnnounceState('idle');
    setAnnouncePreview(null);
    setAnnounceResult(null);
  };

  const selected = items.find((i) => i.id === selectedId);
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto text-white">
      {/* Back to Dashboard */}
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-teal-300 mb-4 transition"
        data-testid="cu-back-to-dashboard"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-teal-400" />
            Client Updates
            {unreadCount > 0 && (
              <span
                className="ml-1 text-xs bg-teal-500 text-white px-2 py-0.5 rounded-full font-semibold"
                data-testid="cu-unread-badge"
              >
                {unreadCount} new
              </span>
            )}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Log of what your clients have reported back from the credit bureaus.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-lg transition"
          data-testid="cu-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Shareable link banner */}
      <div className="bg-gradient-to-r from-teal-500/15 to-cyan-500/10 border border-teal-500/25 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wider text-teal-300 font-semibold mb-1">
              Shareable Client Link
            </div>
            <div
              className="text-sm text-slate-200 font-mono break-all"
              data-testid="cu-public-url"
            >
              {publicUrl}
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              Send this to any client — they can only submit an update, they cannot see any
              other part of your app.{' '}
              <span className="text-teal-300 font-medium">
                Use the two buttons to send the description and the clickable link as separate texts/emails.
              </span>
            </p>
          </div>
          <div className="flex flex-col gap-2 items-stretch sm:items-end">
            <button
              onClick={copyMessageText}
              className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg transition font-semibold ${
                copied && copiedKind === "message"
                  ? "bg-emerald-500 text-white"
                  : "bg-teal-500 hover:bg-teal-400 text-white"
              }`}
              data-testid="cu-copy-message"
              title="Copy just the description paragraph — paste as the FIRST message to your client"
            >
              {copied && copiedKind === "message" ? (
                <>
                  <Check className="w-4 h-4" /> Message copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> 1. Copy message
                </>
              )}
            </button>
            <button
              onClick={copyLinkOnly}
              className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg transition font-semibold ${
                copied && copiedKind === "link"
                  ? "bg-emerald-500 text-white"
                  : "bg-fuchsia-500 hover:bg-fuchsia-400 text-white"
              }`}
              data-testid="cu-copy-link-only"
              title="Copy just the URL — paste as the SECOND message so it appears as a big tappable link"
            >
              {copied && copiedKind === "link" ? (
                <>
                  <Check className="w-4 h-4" /> Link copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> 2. Copy link only
                </>
              )}
            </button>
            <button
              onClick={copyLink}
              className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg transition font-medium ${
                copied && copiedKind === "both"
                  ? "bg-emerald-500/80 text-white"
                  : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700"
              }`}
              data-testid="cu-copy-link"
              title="Copy both — message + link glued together in one paste"
            >
              {copied && copiedKind === "both" ? (
                <>
                  <Check className="w-3.5 h-3.5" /> Both copied!
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" /> Copy both (combined)
                </>
              )}
            </button>
            {/* Feb 2026 — QR code fallback for clients whose phones show
                "Could not find an appropriate application" when tapping
                the raw URL in Google Messages / RCS.  Screenshot & send.  */}
            <button
              onClick={() => { setQrUrl(publicUrl); setQrOpen(true); }}
              className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg transition font-medium bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-500/40"
              data-testid="cu-open-qr"
              title="Generate a QR code the client can scan with their camera — bypasses Android link errors"
            >
              <QrCode className="w-3.5 h-3.5" /> QR code
            </button>
          </div>
        </div>

        {/* ── Dispute-status guide link — separate flow ────────────── */}
        <div className="mt-4 pt-4 border-t border-teal-400/20">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-semibold mb-2">
            📋 Dispute-Status Guide Link (send this so clients can check their own credit reports)
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              readOnly
              value={`${PORTAL_ORIGIN}/dispute-status.html`}
              onClick={(e) => e.target.select()}
              className="flex-1 min-w-[220px] px-3 py-2 bg-zinc-900 border border-fuchsia-500/40 rounded-md text-fuchsia-100 text-sm font-mono"
              data-testid="cu-status-guide-url"
            />
            <button
              onClick={() => {
                const url = `${PORTAL_ORIGIN}/dispute-status.html`;
                if (navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(url);
                } else {
                  const t = document.createElement('textarea'); t.value = url;
                  document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t);
                }
                setCopied(true);
                setCopiedKind('guide');
                setTimeout(() => setCopied(false), 1600);
              }}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-bold"
              data-testid="cu-status-guide-copy"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <a
              href={`sms:?body=${encodeURIComponent("How to check the status of your dispute on all 3 credit bureaus: " + PORTAL_ORIGIN + '/dispute-status.html')}`}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-bold"
              data-testid="cu-status-guide-text"
            >
              Text
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent('How to check the status of your dispute')}&body=${encodeURIComponent("Hey — here's a page that walks you through checking your dispute status on all 3 bureaus (Experian, TransUnion, Equifax): " + PORTAL_ORIGIN + '/dispute-status.html')}`}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-orange-500 hover:bg-orange-400 text-white text-sm font-bold"
              data-testid="cu-status-guide-email"
            >
              Email
            </a>
          </div>
        </div>

        {/* ── Beautiful HTML "click here" button email invite ────────── */}
        <div className="mt-4 pt-4 border-t border-teal-400/20">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-semibold mb-1 flex items-center gap-1.5">
                <span>✨</span> Or send a beautiful HTML email invite
              </div>
              <p className="text-[11px] text-slate-400 mb-2">
                Sends a professionally styled email with a big glowing gradient <strong className="text-fuchsia-300">&ldquo;Tap Here to Open Your Portal&rdquo;</strong> button — impossible to miss, guaranteed clickable.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-stretch">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="client@email.com"
              className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-fuchsia-500 focus:border-fuchsia-500 placeholder-zinc-500"
              data-testid="cu-invite-email"
            />
            <input
              type="text"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Client first name (optional)"
              className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-fuchsia-500 focus:border-fuchsia-500 placeholder-zinc-500"
              data-testid="cu-invite-name"
            />
            <button
              onClick={sendEmailInvite}
              disabled={inviteSending || !inviteEmail.trim()}
              className="inline-flex items-center justify-center gap-2 text-sm px-4 py-2 rounded-lg font-semibold bg-gradient-to-r from-fuchsia-500 via-pink-500 to-orange-500 hover:from-fuchsia-400 hover:via-pink-400 hover:to-orange-400 text-white shadow-md shadow-fuchsia-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cu-send-invite"
            >
              {inviteSending ? (
                <>Sending…</>
              ) : (
                <>
                  <span>✨</span> Send HTML Invite
                </>
              )}
            </button>
          </div>
          {inviteResult && (
            <p
              className={`text-xs mt-2 font-medium ${
                inviteResult.ok ? "text-emerald-400" : "text-red-400"
              }`}
              data-testid="cu-invite-result"
            >
              {inviteResult.ok ? "✓ " : "⚠ "}
              {inviteResult.msg}
            </p>
          )}
        </div>
      </div>

      {/* Blast announcement banner removed Feb 2026 per user request —
          the portal link + explainer is already included in every
          Thursday-night & Friday-morning Step 3 reminder, so the
          one-off blast banner was redundant. */}

      {/* Confirmation modal for the blast */}
      {(announceState !== 'idle') && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="cu-announce-modal"
        >
          <div className="bg-slate-900 border border-purple-500/40 rounded-2xl max-w-lg w-full p-6 shadow-2xl">
            {announceState === 'previewing' && !announcePreview && (
              <div className="text-center py-6 text-slate-300">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-purple-400" />
                Loading recipient list...
              </div>
            )}
            {announcePreview && announceState === 'previewing' && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <Megaphone className="w-5 h-5 text-purple-400" />
                  <h2 className="text-lg font-bold text-white">Confirm Blast</h2>
                </div>
                <p className="text-sm text-slate-300 mb-3">
                  You&apos;re about to send the Client Portal announcement email to{' '}
                  <strong className="text-purple-300">
                    {announcePreview.would_send_to} client{announcePreview.would_send_to === 1 ? '' : 's'}
                  </strong>{' '}
                  in your Quick Reminder folder.
                </p>
                <div className="bg-black/40 border border-white/10 rounded-lg p-3 max-h-56 overflow-y-auto mb-4">
                  {announcePreview.recipients?.length ? (
                    <ul className="text-xs text-slate-300 space-y-1 font-mono">
                      {announcePreview.recipients.map((r, i) => (
                        <li key={i} className="truncate">
                          {r.client_name} &lt;{r.client_email}&gt;
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-500 italic">No recipients with email.</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={closeAnnounce}
                    className="flex-1 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-semibold transition"
                    data-testid="cu-announce-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAnnouncement}
                    disabled={!announcePreview.recipients?.length}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 disabled:opacity-40 text-white text-sm font-bold transition"
                    data-testid="cu-announce-confirm"
                  >
                    <Megaphone className="w-4 h-4" /> Send Now
                  </button>
                </div>
              </>
            )}
            {announceState === 'sending' && (
              <div className="text-center py-6 text-slate-300">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-purple-400" />
                Sending emails... this can take a minute.
              </div>
            )}
            {announceState === 'done' && announceResult?.ok && (
              <div className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                <h2 className="text-lg font-bold text-white mb-1">
                  {announceResult.queued ? 'Blast Started!' : 'Sent!'}
                </h2>
                <p className="text-sm text-slate-300 mb-4">
                  {announceResult.queued ? (
                    <>
                      Sending in the background to <strong>{announceResult.sent}</strong> of{' '}
                      <strong>{announceResult.total}</strong> clients.
                      <br />
                      <span className="text-emerald-300">You&apos;ll get one summary email when it&apos;s done.</span>
                    </>
                  ) : (
                    <>Portal announcement went out to <strong>{announceResult.sent}</strong> of{' '}
                    <strong>{announceResult.total}</strong> clients.</>
                  )}
                </p>
                <button
                  onClick={closeAnnounce}
                  className="px-6 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold transition"
                  data-testid="cu-announce-close-done"
                >
                  Close
                </button>
              </div>
            )}
            {announceState === 'error' && (
              <div className="text-center py-4">
                <XCircle className="w-12 h-12 mx-auto mb-3 text-rose-400" />
                <h2 className="text-lg font-bold text-white mb-1">Something went wrong</h2>
                <p className="text-sm text-slate-300 mb-4">
                  {announceResult?.error || 'Unknown error'}
                </p>
                <button
                  onClick={closeAnnounce}
                  className="px-6 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-bold transition"
                  data-testid="cu-announce-close-error"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab switcher — Updates (existing) vs. Messages (Feb 2026 new). */}
      <div className="flex gap-1 mb-4 border-b border-white/10" data-testid="cu-tabs">
        <button
          type="button"
          onClick={() => setTab('updates')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
            tab === 'updates'
              ? 'border-teal-400 text-teal-300'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
          data-testid="cu-tab-updates"
        >
          <Inbox className="w-4 h-4" /> Status Updates
          {unreadCount > 0 && (
            <span className="ml-1 text-[10px] bg-teal-500 text-white px-1.5 py-0.5 rounded-full font-bold">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setTab('messages')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
            tab === 'messages'
              ? 'border-cyan-400 text-cyan-300'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
          data-testid="cu-tab-messages"
        >
          <MessageSquare className="w-4 h-4" /> 📬 Messages
          {questionsUnread > 0 && (
            <span className="ml-1 text-[10px] bg-cyan-500 text-white px-1.5 py-0.5 rounded-full font-bold">
              {questionsUnread}
            </span>
          )}
        </button>
      </div>

      {/* MESSAGES TAB — client-submitted questions inbox. */}
      {tab === 'messages' && (
        <MessagesInbox
          questions={questions}
          loading={questionsLoading}
          selectedId={selectedQuestionId}
          onSelect={selectQuestion}
          onDelete={deleteQuestion}
          onMarkReplied={markQuestionReplied}
          onRefresh={loadQuestions}
        />
      )}

      {/* UPDATES TAB — the existing status-update inbox. */}
      {tab === 'updates' && (
      <>
      {/* Folder filter — Inbox (unchecked) vs. Checked (reviewed).
          Feb 2026 user request: keep the main list clean so only the
          updates I still need to review show up, then move each one
          to "Checked" once I hit Done inside the detail modal. */}
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => setFolder('inbox')}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition ${
            folder === 'inbox'
              ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30'
              : 'bg-white/5 text-slate-300 hover:bg-white/10'
          }`}
          data-testid="cu-folder-inbox"
        >
          📥 Inbox ({items.filter((i) => !i.checked).length})
        </button>
        <button
          onClick={() => setFolder('checked')}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition ${
            folder === 'checked'
              ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
              : 'bg-white/5 text-slate-300 hover:bg-white/10'
          }`}
          data-testid="cu-folder-checked"
        >
          ✅ Checked ({items.filter((i) => i.checked).length})
        </button>
      </div>
      {/* List (full-width — detail now opens as a modal on click) */}
      <div className="grid grid-cols-1 gap-4">
        {/* List */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-sm text-slate-300 font-semibold">
            {folder === 'inbox' ? (
              <><Inbox className="w-4 h-4" /> Inbox ({items.filter((i) => !i.checked).length})</>
            ) : (
              <><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Checked ({items.filter((i) => i.checked).length})</>
            )}
          </div>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="cu-list">
            {loading ? (
              <div className="p-8 text-center text-slate-400 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : (folder === 'inbox' ? items.filter((i) => !i.checked) : items.filter((i) => i.checked)).length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                {folder === 'inbox'
                  ? 'Nothing new to review — everything checked!'
                  : 'No checked updates yet. Hit Done inside an update to move it here.'}
              </div>
            ) : (
              (folder === 'inbox' ? items.filter((i) => !i.checked) : items.filter((i) => i.checked)).map((item) => (
                <button
                  key={item.id}
                  onClick={() => selectItem(item)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] transition ${
                    selectedId === item.id ? 'bg-white/[0.06]' : ''
                  }`}
                  data-testid={`cu-item-${item.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {item.read ? (
                          <MailOpen className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                        ) : (
                          <Mail className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                        )}
                        <span
                          className={`text-sm truncate ${
                            item.read ? 'text-slate-300' : 'text-white font-semibold'
                          }`}
                        >
                          {item.firstName} {item.lastName}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 flex gap-3 flex-wrap">
                        <span>✓ {item.accountsRemoved?.length || 0}</span>
                        <span>⏳ {item.accountsPending?.length || 0}</span>
                        <span>✗ {item.accountsRemaining?.length || 0}</span>
                        {item.accountsNew && item.accountsNew.length > 0 && (
                          <span className="text-amber-300 font-semibold" title="New accounts on file">
                            🆕 {item.accountsNew.length}
                          </span>
                        )}
                        {item.removalFee > 0 && (
                          <span className="text-emerald-300 font-bold" title={`Removal requested — $${item.removalFee}`}>
                            💰 ${item.removalFee}
                          </span>
                        )}
                        {item.attachments && item.attachments.length > 0 && (
                          <span title={`${item.attachments.length} attached`}>
                            📎 {item.attachments.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 whitespace-nowrap">
                      {new Date(item.submittedAt).toLocaleDateString()}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Detail — Feb 2026 refactored to a modal overlay so mobile
            users don't have to scroll back up after reviewing each
            update.  The Done / X button inside the modal moves the
            update to the Checked folder. */}
        {detailOpen && selected && (
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-6 overflow-y-auto"
            data-testid="cu-detail-modal"
            onClick={(e) => {
              // Click outside the modal closes it without marking checked
              if (e.target === e.currentTarget) {
                setDetailOpen(false);
                setSelectedId(null);
              }
            }}
          >
            <div className="bg-slate-950 border border-white/10 rounded-2xl w-full max-w-3xl my-4 sm:my-8 relative shadow-2xl shadow-black/60" onClick={(e) => e.stopPropagation()}>
              {/* Sticky top bar with X + Done buttons so they're always in reach */}
              <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-3 bg-slate-950/95 backdrop-blur-sm border-b border-white/10 rounded-t-2xl">
                <div className="text-xs uppercase tracking-wider text-teal-300 font-bold">
                  Client Update
                </div>
                <div className="flex items-center gap-2">
                  {!selected.checked && (
                    <button
                      onClick={() => markChecked(selected.id, true)}
                      className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold px-4 py-2 rounded-lg shadow-lg shadow-emerald-500/30"
                      data-testid="cu-mark-checked-btn"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Done
                    </button>
                  )}
                  {selected.checked && (
                    <>
                      <button
                        onClick={() => markChecked(selected.id, false)}
                        className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-400/50 hover:bg-amber-500/30 text-amber-100 text-xs font-bold px-3 py-2 rounded-lg"
                        data-testid="cu-mark-unchecked-btn"
                        title="Move back to Inbox"
                      >
                        ↺ Move back to Inbox
                      </button>
                      <button
                        onClick={() => remove(selected.id)}
                        className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-bold px-3 py-2 rounded-lg"
                        data-testid="cu-delete-permanently-btn"
                        title="Permanently delete this update"
                      >
                        🗑 Delete Permanently
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      setDetailOpen(false);
                      setSelectedId(null);
                    }}
                    className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white font-bold text-lg"
                    data-testid="cu-close-modal"
                    title="Close (without marking checked)"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="p-5 md:p-6">
              {(() => {
                // wrap the entire existing detail body so the modal
                // keeps the original layout unchanged.  The if-nested
                // block below runs when `selected` exists.
                return null;
              })()}
            <div data-testid="cu-detail">
              <div className="flex items-start justify-between gap-3 mb-5">
                <div>
                  <h2 className="text-2xl font-bold">
                    {selected.firstName} {selected.lastName}
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">
                    Submitted {fmt(selected.submittedAt)}
                    {selected.checked && (
                      <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-bold uppercase">
                        <CheckCircle2 className="w-3 h-3" /> Checked
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Auto-link badge — appears when this submission was
                  matched to a Quick Reminder client at submit time. */}
              {selected.linkedClientId && selected.linkedClientName && (
                <div
                  className="mb-4 inline-flex items-center gap-2 rounded-full bg-cyan-500/15 border border-cyan-400/30 px-3 py-1 text-xs text-cyan-200"
                  data-testid="cu-linked-client-pill"
                  title={selected.linkedClientEmail || ''}
                >
                  <Link2 className="w-3.5 h-3.5" />
                  Linked to client:&nbsp;
                  <span className="font-semibold text-cyan-100">
                    {selected.linkedClientName}
                  </span>
                </div>
              )}

              {/* ─── Per-bureau breakdown (Feb 2026) ───────────────
                  When the client filled in the per-bureau section on
                  the portal, show one card per bureau with color-coded
                  Removed / Pending / Remaining rows.  Falls back to
                  the flat 3-block layout for older submissions or
                  clients who used the combined fallback fields. */}
              {(() => {
                const bs = selected.bureauStatus || {};
                const usedBureaus = ['experian', 'transunion', 'equifax'].filter(
                  (b) => {
                    const v = bs[b] || {};
                    return (v.removed?.length || v.pending?.length || v.remaining?.length);
                  }
                );

                if (usedBureaus.length === 0) {
                  return (
                    <>
                      <StatusBlock
                        icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                        label="Removed"
                        color="emerald"
                        items={selected.accountsRemoved}
                      />
                      <StatusBlock
                        icon={<Clock className="w-4 h-4 text-amber-400" />}
                        label="Pending"
                        color="amber"
                        items={selected.accountsPending}
                      />
                      <StatusBlock
                        icon={<XCircle className="w-4 h-4 text-rose-400" />}
                        label="Remaining"
                        color="rose"
                        items={selected.accountsRemaining}
                      />
                    </>
                  );
                }

                const bureauMeta = {
                  experian:   { label: 'EXPERIAN',   grad: 'from-indigo-500 via-blue-500 to-cyan-500' },
                  transunion: { label: 'TRANSUNION', grad: 'from-red-500 via-rose-500 to-pink-500' },
                  equifax:    { label: 'EQUIFAX',    grad: 'from-amber-500 via-orange-500 to-red-500' },
                };
                // eslint-disable-next-line react/no-unstable-nested-components
                const StatusPill = ({ items, color, icon, label }) => {
                  if (!items || items.length === 0) {
                    return (
                      <div className={`text-[11px] uppercase tracking-wider font-bold text-${color}-300 mb-1 flex items-center gap-1.5`}>
                        {icon} {label} <span className="text-zinc-500 font-normal normal-case tracking-normal italic">(none)</span>
                      </div>
                    );
                  }
                  return (
                    <div className="mb-2">
                      <div className={`text-[11px] uppercase tracking-wider font-bold text-${color}-300 mb-1 flex items-center gap-1.5`}>
                        {icon} {label}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((it, i) => (
                          <span key={i} className={`inline-block px-2 py-0.5 rounded-md bg-${color}-500/15 border border-${color}-400/30 text-${color}-100 text-xs`}>
                            {it}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="space-y-3">
                    {usedBureaus.map((bkey) => {
                      const v = bs[bkey] || {};
                      const meta = bureauMeta[bkey];
                      return (
                        <div key={bkey} className="rounded-xl overflow-hidden border border-white/10" data-testid={`cu-bureau-detail-${bkey}`}>
                          <div className={`bg-gradient-to-r ${meta.grad} px-4 py-2 text-white text-sm font-black tracking-wider`}>
                            {meta.label}
                          </div>
                          <div className="bg-black/30 p-3 space-y-2">
                            <StatusPill items={v.removed}   color="emerald" icon={<CheckCircle2 className="w-3 h-3" />} label="Removed" />
                            <StatusPill items={v.pending}   color="amber"   icon={<Clock className="w-3 h-3" />}       label="Pending" />
                            <StatusPill items={v.remaining} color="rose"    icon={<XCircle className="w-3 h-3" />}     label="Remaining" />
                          </div>
                        </div>
                      );
                    })}
                    {/* Fallback flat entries if they were also filled */}
                    {(selected.accountsRemoved?.length || selected.accountsPending?.length || selected.accountsRemaining?.length) ? (
                      <details className="rounded-lg border border-white/10 bg-white/5">
                        <summary className="cursor-pointer px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-zinc-400 select-none">
                          ▸ Also listed (combined — no bureau specified)
                        </summary>
                        <div className="p-3 pt-1 space-y-2">
                          <StatusBlock icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />} label="Removed" color="emerald" items={selected.accountsRemoved} />
                          <StatusBlock icon={<Clock className="w-4 h-4 text-amber-400" />}          label="Pending" color="amber"   items={selected.accountsPending} />
                          <StatusBlock icon={<XCircle className="w-4 h-4 text-rose-400" />}         label="Remaining" color="rose"  items={selected.accountsRemaining} />
                        </div>
                      </details>
                    ) : null}
                  </div>
                );
              })()}

              {selected.accountsNew && selected.accountsNew.length > 0 && (
                <div className="mt-4 rounded-lg border-l-4 border-amber-500 bg-amber-500/10 pl-3 pr-3 py-2" data-testid="cu-new-accounts-block">
                  <div className="text-xs uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-2 text-amber-300">
                    <PlusCircle className="w-4 h-4" /> New Accounts on Credit File ({selected.accountsNew.length})
                  </div>
                  <ul className="bg-white/5 border border-white/10 rounded-lg divide-y divide-white/5">
                    {selected.accountsNew.map((s, i) => (
                      <li key={i} className="px-3 py-2 text-sm text-amber-100">
                        {s}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[10px] text-amber-200/70 italic mt-1">
                    ⚠️ Client flagged these as brand-new items on their credit file — investigate for possible ID theft or unauthorized inquiries.
                  </p>
                </div>
              )}

              {/* Removal request highlight — only when client opted in + quoted a fee */}
              {selected.removalFee > 0 && (
                <div
                  className="mt-4 rounded-xl p-4 bg-gradient-to-br from-emerald-500 to-cyan-600 text-white shadow-lg shadow-emerald-500/30"
                  data-testid="cu-removal-request-block"
                >
                  <div className="text-[11px] uppercase tracking-widest font-bold opacity-90">
                    💰 Removal Requested
                  </div>
                  <div className="text-lg font-bold mt-1">
                    Client wants {selected.accountsNew?.length} new account{selected.accountsNew?.length === 1 ? '' : 's'} removed
                  </div>
                  <div className="text-3xl font-black mt-2 tracking-tight" data-testid="cu-removal-fee">
                    Quoted Fee: ${selected.removalFee}
                  </div>
                  <div className="text-xs opacity-90 mt-1">
                    $150 paperwork + $100 × {selected.accountsNew?.length} account{selected.accountsNew?.length === 1 ? '' : 's'}
                  </div>
                  <div className="text-[10px] opacity-80 mt-2 italic">
                    Client has been shown the payment options — wait for their screenshot text.
                  </div>
                </div>
              )}

              {selected.reasonRemaining && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                    Why remaining
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-slate-200 whitespace-pre-wrap">
                    {selected.reasonRemaining}
                  </div>
                </div>
              )}

              {selected.additionalInfo && (
                <div className="mt-4" data-testid="cu-additional-info-block">
                  <div className="text-xs uppercase tracking-wider text-indigo-300 font-semibold mb-1.5 flex items-center gap-1.5">
                    💬 Additional Information from Client
                  </div>
                  <div className="bg-indigo-500/10 border-l-4 border-indigo-400 rounded-lg p-3 text-sm text-indigo-100 whitespace-pre-wrap">
                    {selected.additionalInfo}
                  </div>
                </div>
              )}

              {/* Scores */}
              {(selected.scoreExperian ||
                selected.scoreEquifax ||
                selected.scoreTransunion) && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                    Credit Scores
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <ScoreCard label="Experian" value={selected.scoreExperian} />
                    <ScoreCard label="Equifax" value={selected.scoreEquifax} />
                    <ScoreCard label="TransUnion" value={selected.scoreTransunion} />
                  </div>
                </div>
              )}

              {/* Score-over-time chart — visible when this client has
                  2+ submissions with any bureau score.  Plots all three
                  bureaus on the same axis so trends are comparable. */}
              {history.points.length >= 2 && (
                <ScoreTrendChart points={history.points} />
              )}

              {/* Attachments — photos of docs from bureaus/creditors.
                  2026-02-28 (final user request): images must render
                  INLINE directly under the message text.  No lightbox,
                  no preview click.  The user complained the modal
                  viewer kept getting stuck on iOS and forced app
                  restarts.  So we now stack each image full-width
                  right in the feed and leave non-image files (PDFs
                  etc.) as a simple "Open file" link. */}
              {selected.attachments && selected.attachments.length > 0 && (
                <div className="mt-5" data-testid="cu-attachments">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3 flex items-center gap-1.5">
                    📎 Attached ({selected.attachments.length})
                  </div>
                  <div className="flex flex-col gap-3">
                    {selected.attachments.map((att, idx) => {
                      const url = `${API_URL}/api/client-updates/attachment/${selected.id}/${att.filename}`;
                      const IMG_EXT_RE = /\.(png|jpe?g|gif|webp|heic|heif|bmp|avif|tiff?|svg)$/i;
                      const looksLikeImageByName = IMG_EXT_RE.test(att.originalName || att.filename || '');
                      const ct = (att.contentType || '').toLowerCase();
                      const ctIsImage = ct.startsWith('image/');
                      const ctIsGeneric = !ct || ct === 'application/octet-stream' || ct === 'binary/octet-stream';
                      const ctIsDoc = ct === 'application/pdf' || ct.startsWith('application/vnd') || ct.startsWith('text/');
                      const originallyImage = ctIsImage || looksLikeImageByName || (ctIsGeneric && !ctIsDoc);
                      const isImage = originallyImage && !brokenImages.has(url);
                      if (isImage) {
                        return (
                          <figure
                            key={idx}
                            className="rounded-xl overflow-hidden border border-white/10 bg-slate-900"
                            data-testid={`cu-attachment-${idx}`}
                          >
                            <img
                              src={url}
                              alt={att.originalName || `attachment-${idx + 1}`}
                              className="w-full h-auto max-h-[70vh] object-contain bg-black"
                              loading="lazy"
                              onError={() => {
                                setBrokenImages((prev) => {
                                  if (prev.has(url)) return prev;
                                  const next = new Set(prev);
                                  next.add(url);
                                  return next;
                                });
                              }}
                            />
                            {att.originalName && (
                              <figcaption className="px-3 py-2 text-[11px] text-slate-400 truncate border-t border-white/10">
                                {att.originalName}
                              </figcaption>
                            )}
                          </figure>
                        );
                      }
                      // Non-image (PDF etc.) OR an image that failed to
                      // render — show a compact link row that opens the
                      // file in a new tab.  No modal, no getting stuck.
                      return (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-white/10 bg-slate-900 hover:border-teal-400/60 hover:bg-slate-800 transition text-sm text-slate-200"
                          data-testid={`cu-attachment-${idx}`}
                        >
                          <span className="text-2xl leading-none">
                            {originallyImage ? '🖼️' : '📄'}
                          </span>
                          <span className="flex-1 truncate">
                            {att.originalName || att.filename || `attachment-${idx + 1}`}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-500/25 text-teal-200 border border-teal-400/40 font-semibold">
                            Open
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Reply-by-email CTA — Feb 2026 user request: after reviewing
                  a client's update + scores, tap Reply to fire off a Gmail
                  compose window pre-filled with the client's email + a
                  contextual subject/quoted body.  Uses the client-record
                  email (linkedClientEmail) OR the submitter email
                  (selected.email fallback for public submissions). */}
              {(() => {
                const clientEmail = selected.linkedClientEmail || selected.email || '';
                const displayName = selected.linkedClientName
                  || `${selected.firstName || ''} ${selected.lastName || ''}`.trim();
                const subject = `Re: Your Credit Update — ${displayName}`;
                // Build a quoted context body so you can just start typing above it.
                const summaryLines = [];
                if (Array.isArray(selected.accountsRemoved) && selected.accountsRemoved.length) {
                  summaryLines.push(`Removed: ${selected.accountsRemoved.join(', ')}`);
                }
                if (Array.isArray(selected.accountsPending) && selected.accountsPending.length) {
                  summaryLines.push(`Pending: ${selected.accountsPending.join(', ')}`);
                }
                if (Array.isArray(selected.accountsRemaining) && selected.accountsRemaining.length) {
                  summaryLines.push(`Remaining: ${selected.accountsRemaining.join(', ')}`);
                }
                const scoreLine = [
                  selected.scoreExperian && `Experian ${selected.scoreExperian}`,
                  selected.scoreEquifax && `Equifax ${selected.scoreEquifax}`,
                  selected.scoreTransunion && `TransUnion ${selected.scoreTransunion}`,
                ].filter(Boolean).join(' · ');
                if (scoreLine) summaryLines.push(`Scores → ${scoreLine}`);
                const body = summaryLines.length
                  ? `\n\n\n---\nYour update summary:\n${summaryLines.join('\n')}\n`
                  : '';
                const mailto = clientEmail
                  ? `mailto:${clientEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
                  : '';
                return (
                  <div className="mt-6 pt-5 border-t border-white/10" data-testid="cu-reply-block">
                    {clientEmail ? (
                      <a
                        href={mailto}
                        onClick={() => {
                          // Feb 2026 — when the admin actively hits Reply,
                          // auto-mark the update Checked so it moves to the
                          // Checked folder even if they forget to hit Done
                          // in Gmail.  If it's already Checked this is a no-op.
                          if (selected && !selected.checked) {
                            markChecked(selected.id, true);
                          }
                        }}
                        className="inline-flex items-center justify-center gap-2 w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold py-3 px-5 rounded-xl transition-all shadow-lg shadow-cyan-500/20"
                        data-testid="cu-reply-btn"
                      >
                        <Mail className="w-4 h-4" />
                        Reply by Email
                        <span className="text-xs font-normal opacity-80">
                          → {clientEmail}
                        </span>
                      </a>
                    ) : (
                      <div className="w-full text-center rounded-xl border border-dashed border-amber-400/40 bg-amber-500/10 py-3 px-4 text-sm text-amber-200">
                        ⚠ No email on file for this client — add one in Quick Reminders to enable Reply.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
              </div>{/* end modal padded body */}
            </div>{/* end modal card */}
          </div>
        )}
      </div>
      </>
      )}

      {/* ── QR Code Modal ─────────────────────────────────────────────
          Feb 2026 — Fixes "Could not find an appropriate application"
          Android error some clients hit when tapping raw portal URLs.
          Owner types/pastes any URL, gets a scannable QR they can screen-
          shot and send.  Camera app opens the URL in the client's default
          browser, sidestepping Google Messages' link handler entirely. */}
      {qrOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="qr-modal"
          onClick={() => setQrOpen(false)}
        >
          <div
            className="bg-slate-900 border border-amber-500/40 rounded-2xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setQrOpen(false)}
              className="absolute top-3 right-3 text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
              aria-label="Close"
              data-testid="qr-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-bold text-white">Portal QR Code</h3>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              Type or paste any URL below.  Screenshot the code and send it — clients scan with their camera and it opens the link in their browser (no Android app-link error).
            </p>
            <label className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold block mb-1.5">
              URL
            </label>
            <input
              type="text"
              value={qrUrl}
              onChange={(e) => setQrUrl(e.target.value)}
              placeholder="https://…"
              className="w-full px-3 py-2 mb-4 bg-black/40 border border-amber-500/40 rounded-lg text-slate-100 text-sm font-mono focus:outline-none focus:border-amber-400"
              data-testid="qr-url-input"
              autoFocus
              spellCheck={false}
            />
            <div className="bg-white rounded-xl p-4 flex items-center justify-center" data-testid="qr-canvas-wrap">
              {qrUrl.trim() ? (
                <QRCodeCanvas
                  ref={qrCanvasRef}
                  value={qrUrl.trim()}
                  size={256}
                  level="H"
                  includeMargin={false}
                />
              ) : (
                <div className="text-slate-500 text-xs italic py-16">Paste a URL above to generate the QR code</div>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setQrUrl(publicUrl)}
                className="flex-1 text-xs px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-medium transition"
                data-testid="qr-reset"
              >
                Reset to portal link
              </button>
              <button
                onClick={() => {
                  const canvas = qrCanvasRef.current;
                  if (!canvas) return;
                  const url = canvas.toDataURL('image/png');
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'client-portal-qr.png';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
                disabled={!qrUrl.trim()}
                className="flex-1 inline-flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold transition disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="qr-download"
              >
                <Download className="w-3.5 h-3.5" /> Download PNG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2026-02-28 — Lightbox/preview modal REMOVED per user request.
          Images now render inline directly under the message text (see
          attachments block above).  Non-image files open in a new tab
          via a plain <a href> — no more getting stuck on iOS. */}
    </div>
  );
}

/**
 * MessagesInbox — client-submitted questions.
 * Left rail lists questions; right pane shows selected details with a
 * "Reply via email" button (mailto:) so the owner can hit Reply in
 * Gmail and respond directly (this triggers the same behavior as
 * clicking Reply in the notification email — the client's email is
 * already the Reply-To on the notification).
 */
function MessagesInbox({ questions, loading, selectedId, onSelect, onDelete, onMarkReplied, onRefresh }) {
  const selected = questions.find((q) => q.id === selectedId);
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
      <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between text-sm text-slate-300 font-semibold">
          <span className="inline-flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Messages ({questions.length})
          </span>
          <button
            type="button"
            onClick={onRefresh}
            className="text-slate-400 hover:text-cyan-300 transition"
            title="Refresh"
            data-testid="cu-messages-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto" data-testid="cu-messages-list">
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : questions.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              No messages yet. Clients can send you questions from{' '}
              <span className="text-cyan-300 font-mono text-[11px]">/client-update</span>.
            </div>
          ) : (
            questions.map((q) => (
              <button
                key={q.id}
                onClick={() => onSelect(q)}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] transition ${
                  selectedId === q.id ? 'bg-white/[0.06]' : ''
                }`}
                data-testid={`cu-message-item-${q.id}`}
              >
                <div className="flex items-center gap-2">
                  {q.read ? (
                    <MailOpen className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                  ) : (
                    <Mail className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                  )}
                  <span className={`text-sm truncate ${q.read ? 'text-slate-300' : 'text-white font-semibold'}`}>
                    {q.firstName} {q.lastName}
                  </span>
                  {q.replied && (
                    <span className="text-[10px] bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 px-1.5 py-0.5 rounded-full ml-auto flex-shrink-0">
                      Replied
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-slate-400 mt-1 truncate">{q.subject}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {new Date(q.submittedAt).toLocaleDateString()}
                  {q.bureau && <span className="ml-2 capitalize text-cyan-400">· {q.bureau}</span>}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 md:p-6 min-h-[300px]">
        {selected ? (
          <div className="space-y-4" data-testid="cu-message-detail">
            <div>
              <h2 className="text-xl font-bold text-white">{selected.subject}</h2>
              <div className="text-sm text-slate-400 mt-1">
                From <strong className="text-slate-200">{selected.firstName} {selected.lastName}</strong>
                {selected.email && (
                  <> · <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`} className="text-cyan-300 hover:text-cyan-200 underline">{selected.email}</a></>
                )}
                {selected.bureau && (
                  <> · <span className="text-cyan-400 capitalize">{selected.bureau}</span></>
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{fmt(selected.submittedAt)}</div>
            </div>
            <div className="bg-slate-900/60 border-l-2 border-cyan-400/60 rounded-md p-4 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
              {selected.message}
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
              {selected.email && (
                <a
                  href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}&body=%0A%0A---%0AOriginal message from ${encodeURIComponent(selected.firstName + ' ' + selected.lastName)}:%0A${encodeURIComponent(selected.message)}`}
                  onClick={() => onMarkReplied(selected.id)}
                  className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
                  data-testid="cu-message-reply-mailto"
                >
                  ✉ Reply via Email
                </a>
              )}
              <button
                type="button"
                onClick={() => onMarkReplied(selected.id)}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-slate-100 text-sm font-semibold px-4 py-2 rounded-lg transition"
                data-testid="cu-message-mark-replied"
              >
                Mark as Replied
              </button>
              <button
                type="button"
                onClick={() => onDelete(selected.id)}
                className="ml-auto inline-flex items-center gap-2 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-400/30 text-rose-300 text-sm font-semibold px-4 py-2 rounded-lg transition"
                data-testid="cu-message-delete"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
            {!selected.email && (
              <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded-md px-3 py-2">
                ⚠ No email captured on this message — you can&apos;t reply directly.  Look them up in your Quick Reminders list and reach out from there.
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-500 text-sm">
            <MessageSquare className="w-10 h-10 mb-3 opacity-40" />
            Select a message to view details.
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBlock({ icon, label, color, items }) {
  const colorMap = {
    emerald: 'text-emerald-300',
    amber: 'text-amber-300',
    rose: 'text-rose-300',
  };
  return (
    <div className="mt-4">
      <div
        className={`text-xs uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-2 ${colorMap[color]}`}
      >
        {icon} {label} ({items?.length || 0})
      </div>
      {items && items.length > 0 ? (
        <ul className="bg-white/5 border border-white/10 rounded-lg divide-y divide-white/5">
          {items.map((s, i) => (
            <li key={i} className="px-3 py-2 text-sm text-slate-200">
              {s}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs text-slate-500 italic pl-1">(none reported)</div>
      )}
    </div>
  );
}

function ScoreCard({ label, value }) {
  return (
    <div className="bg-teal-500/10 border border-teal-500/25 rounded-lg p-2.5 text-center">
      <div className="text-[10px] uppercase tracking-wider text-teal-300 font-semibold">
        {label}
      </div>
      <div className="text-lg font-bold text-white mt-0.5">{value || '—'}</div>
    </div>
  );
}


/**
 * ScoreTrendChart — pure-SVG per-bureau score trend for one client.
 * Draws three lines (Experian red, Equifax blue, TransUnion green)
 * on a shared 300-850 Y-axis, dropping missing data points cleanly.
 * No chart library required — keeps the bundle lean.
 */
function ScoreTrendChart({ points }) {
  const width = 520;
  const height = 200;
  const padTop = 16;
  const padBottom = 34;
  const padLeft = 40;
  const padRight = 16;
  const plotW = width - padLeft - padRight;
  const plotH = height - padTop - padBottom;

  const bureaus = [
    { key: 'experian', label: 'Experian', color: '#f87171' },
    { key: 'equifax', label: 'Equifax', color: '#60a5fa' },
    { key: 'transunion', label: 'TransUnion', color: '#34d399' },
  ];

  // Derive Y-axis range from actual values (pad ±20) so short spans
  // don't render as a flat line across a 300-850 axis.
  const allVals = [];
  points.forEach((p) => {
    bureaus.forEach((b) => {
      if (p[b.key]) allVals.push(p[b.key]);
    });
  });
  const yMin = Math.max(300, Math.min(...allVals) - 20);
  const yMax = Math.min(850, Math.max(...allVals) + 20);
  const ySpan = Math.max(1, yMax - yMin);

  const xFor = (i) => padLeft + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const yFor = (v) => padTop + plotH - ((v - yMin) / ySpan) * plotH;

  // Format submittedAt → short date for x-axis ticks
  const shortDate = (iso) => {
    try {
      const d = new Date(iso);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    } catch {
      return '';
    }
  };

  // Y-axis gridlines at 25% intervals
  const yTicks = 4;
  const gridlines = Array.from({ length: yTicks + 1 }, (_, i) => {
    const v = yMin + (ySpan * i) / yTicks;
    const y = yFor(v);
    return { v: Math.round(v), y };
  });

  return (
    <div className="mt-5" data-testid="cu-score-trend-chart">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
        <TrendingUp className="w-3.5 h-3.5 text-teal-300" />
        Score History ({points.length} submission{points.length === 1 ? '' : 's'})
      </div>
      <div className="bg-white/[0.04] border border-white/10 rounded-xl p-3 overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-auto"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Gridlines + Y-axis labels */}
          {gridlines.map((g, i) => (
            <g key={i}>
              <line
                x1={padLeft}
                x2={width - padRight}
                y1={g.y}
                y2={g.y}
                stroke="rgba(255,255,255,0.08)"
                strokeDasharray="3 4"
              />
              <text
                x={padLeft - 6}
                y={g.y + 3}
                fontSize="9"
                fill="rgba(203,213,225,0.55)"
                textAnchor="end"
              >
                {g.v}
              </text>
            </g>
          ))}

          {/* Per-bureau polylines + data-point dots */}
          {bureaus.map((b) => {
            const segs = [];
            let current = [];
            points.forEach((p, i) => {
              if (p[b.key]) {
                current.push(`${xFor(i)},${yFor(p[b.key])}`);
              } else if (current.length) {
                segs.push(current.join(' '));
                current = [];
              }
            });
            if (current.length) segs.push(current.join(' '));

            return (
              <g key={b.key}>
                {segs.map((s, si) => (
                  <polyline
                    key={si}
                    points={s}
                    fill="none"
                    stroke={b.color}
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                ))}
                {points.map((p, i) =>
                  p[b.key] ? (
                    <g key={i}>
                      <circle
                        cx={xFor(i)}
                        cy={yFor(p[b.key])}
                        r="3.5"
                        fill={b.color}
                        stroke="#0f172a"
                        strokeWidth="1.5"
                      >
                        <title>{`${b.label}: ${p[b.key]} on ${shortDate(p.submittedAt)}`}</title>
                      </circle>
                    </g>
                  ) : null,
                )}
              </g>
            );
          })}

          {/* X-axis date labels — thin them out if there are lots of points */}
          {points.map((p, i) => {
            const step = Math.max(1, Math.ceil(points.length / 6));
            if (i % step !== 0 && i !== points.length - 1) return null;
            return (
              <text
                key={i}
                x={xFor(i)}
                y={height - 14}
                fontSize="9"
                fill="rgba(203,213,225,0.55)"
                textAnchor="middle"
              >
                {shortDate(p.submittedAt)}
              </text>
            );
          })}
        </svg>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 justify-center mt-1 text-[11px]">
          {bureaus.map((b) => (
            <div key={b.key} className="flex items-center gap-1.5 text-slate-300">
              <span
                className="inline-block w-3 h-1 rounded"
                style={{ backgroundColor: b.color }}
              />
              {b.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
